import {
  COST_RANGE_DIMENSIONS,
  type CostRangeDimensionId,
} from './media-unit-cost-range-dimensions.js';

/**
 * Single source of truth for the Katalog nośników column↔filter↔sort parity
 * rule (`catalog-column-filter-parity`). One entry per catalog **field
 * dimension** — adding a new filterable/sortable catalog field means adding
 * ONE entry here, following the same single-source-of-truth intent as
 * `COST_RANGE_DIMENSIONS` (media-unit-cost-range-dimensions.ts).
 *
 * `gate` is a closed TypeScript union (`'none' | 'cost.view' | 'supplier.view'`)
 * — an out-of-union literal is already rejected by `tsc` at compile time, so
 * this is a type-system guarantee, not something a runtime test needs to assert.
 *
 * ── id vs columnId/filterId ──────────────────────────────────────────────
 * `id` is the canonical field id. When the catalog **column** id
 * (`columns.tsx`/`columns-extended.tsx`) or the **filter** field id
 * (`filter-fields.ts`) legitimately differs from `id` — a handful of fields
 * predate this registry with mismatched naming — the override is declared
 * via `columnId`/`filterId`. Use `catalogColumnId`/`catalogFilterId` below to
 * resolve the effective id rather than reading `.id` directly. Notable
 * overrides:
 * - `supplierId` (filter) → column id `supplier` (displays `supplierName`).
 * - `formatCode` (filter) → column id `format` (new hidden column, reverse parity).
 * - `lighting` (filter) → column id `illuminated` (derived boolean display).
 * - `region`/`county`/`agglomeration`/`city` (4 separate columns) → all four
 *   share filter id `location` (the combined "Lokalizacja" grouped-select pill).
 * - `width`/`height` (2 separate range filters) → both share column id
 *   `dimensions` (one composite display column).
 * - `audience` (filter) → column id `audienceEstimate`.
 *
 * ── queryMode ─────────────────────────────────────────────────────────────
 * A small set of filters are query-mode filters, not field projections:
 * `search`, `availability` (availabilityFrom/To), `noPhoto`, `nearPoi`
 * (near_poi_key/value/radius_m/id), `importJobId`, and `administrativeAreaIds`
 * (a rollout-gated administrative-area lookup with no catalog column yet).
 * These are exempt from the column-parity rule in the filter→column
 * direction — see `findCatalogFieldRegistryParityViolations` below.
 */

export type CatalogFieldGate = 'none' | 'cost.view' | 'supplier.view';

export type CatalogFieldFilterKind = 'select' | 'text' | 'range' | 'dateRange' | 'groupedSelect';

export interface CatalogFieldRegistryEntry {
  /** Canonical field id — matches the column id and filter id by default. */
  readonly id: string;
  /** Whether this field dimension backs a catalog column. */
  readonly column: boolean;
  /** Override when the column id differs from `id` (see file doc comment). */
  readonly columnId?: string;
  /** `false` when this field dimension has no filter; otherwise the FilterPanel control kind. */
  readonly filter: false | CatalogFieldFilterKind;
  /** Override when the filter id differs from `id` (see file doc comment). */
  readonly filterId?: string;
  /** Whether this field dimension backs a `MEDIA_UNIT_SORT_KEYS` entry. */
  readonly sort: boolean;
  /** Visibility gate: the scope a caller must hold (unscoped) to see this field's column/filter. */
  readonly gate: CatalogFieldGate;
  /**
   * Query-mode filter: not a single field projection (multi-column search,
   * a window, a boolean toggle, a proximity lookup, ...). Exempt from the
   * filter→column direction of the parity rule. Defaults to `false`.
   */
  readonly queryMode?: boolean;
}

/**
 * Client price dimensions whose values are ALWAYS visible regardless of
 * cost.view (never gated) — `cost.view` gates internal COSTS only, never
 * client PRICES, whatever is shown or charged to the client.
 * catalogPrice/discountedPrice/lastMinutePrice/starterPrice were corrected
 * into this set from the internal-cost set (permission-model correction,
 * CU-86cbgnt38 round 4) — they are client prices, not costs.
 */
const CLIENT_FACING_COST_DIMENSION_IDS: ReadonlySet<CostRangeDimensionId> = new Set([
  'catalogPrice',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
]);

const costDimensionEntries: CatalogFieldRegistryEntry[] = COST_RANGE_DIMENSIONS.map((d) => ({
  id: d.id,
  column: true,
  filter: 'range',
  sort: true,
  gate: CLIENT_FACING_COST_DIMENSION_IDS.has(d.id) ? 'none' : 'cost.view',
}));

export const CATALOG_FIELD_REGISTRY: readonly CatalogFieldRegistryEntry[] = [
  // ─── Query-mode filters — exempt from filter→column parity ───────────────
  { id: 'search', column: false, filter: false, sort: false, gate: 'none', queryMode: true },
  { id: 'availability', column: false, filter: false, sort: false, gate: 'none', queryMode: true },
  { id: 'noPhoto', column: true, filter: false, sort: false, gate: 'none', queryMode: true },
  { id: 'nearPoi', column: false, filter: false, sort: false, gate: 'none', queryMode: true },
  { id: 'importJobId', column: false, filter: false, sort: false, gate: 'none', queryMode: true },
  {
    id: 'administrativeAreaIds',
    column: false,
    filter: 'select',
    sort: false,
    gate: 'none',
    queryMode: true,
  },

  // ─── Standard select/text dimensions ──────────────────────────────────────
  { id: 'mediaType', column: true, filter: 'select', sort: true, gate: 'none' },
  {
    id: 'formatCode',
    column: true,
    columnId: 'format',
    filter: 'select',
    sort: true,
    gate: 'none',
  },
  {
    id: 'supplierId',
    column: true,
    columnId: 'supplier',
    filter: 'select',
    sort: true,
    gate: 'supplier.view',
  },
  { id: 'supplierNumber', column: true, filter: 'text', sort: true, gate: 'supplier.view' },
  // supplierEmail/supplierPhone/supplierContactInfo were removed entirely
  // (CU-86cb1nq7h, client request) — no column, no filter, for any role.
  // These fields belong only in the Suppliers area now. Not registry entries
  // anymore since they are no longer a catalog field dimension at all.
  {
    id: 'lighting',
    column: true,
    columnId: 'illuminated',
    filter: 'select',
    sort: true,
    gate: 'none',
  },
  { id: 'grwId', column: true, filter: 'text', sort: true, gate: 'none' },
  {
    id: 'region',
    column: true,
    filter: 'groupedSelect',
    filterId: 'location',
    sort: true,
    gate: 'none',
  },
  {
    id: 'county',
    column: true,
    filter: 'groupedSelect',
    filterId: 'location',
    sort: true,
    gate: 'none',
  },
  {
    id: 'agglomeration',
    column: true,
    filter: 'groupedSelect',
    filterId: 'location',
    sort: true,
    gate: 'none',
  },
  {
    id: 'city',
    column: true,
    filter: 'groupedSelect',
    filterId: 'location',
    sort: true,
    gate: 'none',
  },
  { id: 'address', column: true, filter: 'text', sort: true, gate: 'none' },
  { id: 'material', column: true, filter: 'select', sort: true, gate: 'none' },
  { id: 'multiformat', column: true, filter: 'select', sort: true, gate: 'none' },
  { id: 'tags', column: true, filter: 'select', sort: true, gate: 'none' },
  { id: 'notes', column: true, filter: 'text', sort: true, gate: 'none' },
  // `status` keeps its sort key (unrelated presentation-only removal,
  // CU-86cb1nq7h — redundant with detailed availability) but has no column
  // or filter for any role anymore; the underlying domain status data and
  // availability views are untouched.
  { id: 'status', column: false, filter: false, sort: true, gate: 'none' },
  { id: 'roadNumber', column: true, filter: 'text', sort: true, gate: 'none' },

  // ─── Numeric range dimensions ──────────────────────────────────────────────
  { id: 'area', column: true, filter: 'range', sort: true, gate: 'none' },
  { id: 'width', column: true, columnId: 'dimensions', filter: 'range', sort: true, gate: 'none' },
  { id: 'height', column: true, columnId: 'dimensions', filter: 'range', sort: true, gate: 'none' },
  {
    id: 'audience',
    column: true,
    columnId: 'audienceEstimate',
    filter: 'range',
    sort: true,
    gate: 'none',
  },
  ...costDimensionEntries,
] as const satisfies readonly CatalogFieldRegistryEntry[];

/** Resolves the effective catalog-column id for a registry entry (see file doc comment). */
export function catalogColumnId(entry: CatalogFieldRegistryEntry): string {
  return entry.columnId ?? entry.id;
}

/** Resolves the effective FilterPanel field id for a registry entry (see file doc comment). */
export function catalogFilterId(entry: CatalogFieldRegistryEntry): string {
  return entry.filterId ?? entry.id;
}

/**
 * Derived (not hand-listed) set of filter param keys gated behind an
 * unscoped `supplier.view` grant — the exact two params
 * `stripSupplierFiltersIfUnauthorized` (apps/api) strips server-side.
 */
export const SUPPLIER_GATED_FILTER_KEYS: readonly string[] = CATALOG_FIELD_REGISTRY.filter(
  (e) => e.gate === 'supplier.view' && e.filter !== false,
).map(catalogFilterId);

/**
 * Validates the registry's own internal self-consistency: every non-`queryMode`
 * entry that declares a column also declares a filter, and vice versa. Returns
 * a human-readable violation string per offending field id, empty when the
 * registry is fully consistent.
 *
 * This checks the REGISTRY DATA itself, not the actual `buildColumns`/
 * `buildFilterFields` output — the web-level registry-driven parity test
 * (task 4.3, `catalog-column-filter-parity/column-filter-parity.spec.ts`)
 * cross-references the real rendered column/filter ids against this registry
 * for each role-gating combination.
 */
export function findCatalogFieldRegistryParityViolations(
  registry: readonly CatalogFieldRegistryEntry[] = CATALOG_FIELD_REGISTRY,
): string[] {
  const violations: string[] = [];
  for (const entry of registry) {
    if (entry.queryMode) continue;
    const hasColumn = entry.column;
    const hasFilter = entry.filter !== false;
    if (hasColumn && !hasFilter) {
      violations.push(`${entry.id}: has a column but no filter`);
    }
    if (hasFilter && !hasColumn) {
      violations.push(`${entry.id}: has a filter but no column`);
    }
  }
  return violations;
}
